import * as rrweb from 'rrweb';
import { v4 as uuidv4 } from 'uuid';
import axios from 'axios';
import { SessionableOptions, SessionMetrics, SessionPayload } from '../types';
import { listenerHandler } from '@rrweb/types';

/**
 * Core Sessionable class for session recording
 */
export class Sessionable {
  private options: Required<SessionableOptions>;
  private events: rrweb.eventWithTime[] = [];
  private stopFn: listenerHandler | null = null;
  private eventsInterval: ReturnType<typeof setInterval> | null = null;
  private metrics: SessionMetrics;
  // Always point to the real API endpoint
  private apiEndpoint = 'https://api.sessionable.com/api/sessions';
  private batchSize = 50;
  private batchInterval = 5000; // ms

  /**
   * Creates a new Sessionable instance
   */
  constructor(options: SessionableOptions) {
    // Set default options
    this.options = {
      apiKey: options.apiKey,
      autoStart: options.autoStart || false,
      metadata: options.metadata || {},
    };

    // Initialize metrics
    this.metrics = {
      sessionId: '',
      eventsRecorded: 0,
      batchesSent: 0,
      batchesSuccessful: 0,
      connectionStatus: 'disconnected',
      lastError: null,
      lastBatchSize: 0,
      lastSuccessfulUpload: null,
      recordingStartTime: new Date(),
      isRecording: false,
    };

    // Automatically start recording if option is set
    if (this.options.autoStart) {
      this.start();
    }
  }

  /**
   * Start recording the session
   */
  public start(): void {
    if (this.metrics.isRecording) return;

    // Generate a new session ID
    const sessionId = uuidv4();
    
    // Store locally if localStorage is available
    try {
      localStorage.setItem('sessionable_session_id', sessionId);
    } catch (e) {
      // Ignore localStorage errors
    }

    // Reset events array
    this.events = [];

    // Update metrics
    this.updateMetrics({
      sessionId,
      recordingStartTime: new Date(),
      eventsRecorded: 0,
      batchesSent: 0,
      batchesSuccessful: 0,
      connectionStatus: 'connecting',
      isRecording: true
    });

    // Start recording with rrweb
    const stop = rrweb.record({
      emit: (event) => {
        // Push event to array
        this.events.push(event);

        // Update metrics
        this.updateMetrics({
          eventsRecorded: this.metrics.eventsRecorded + 1
        });

        // Send batch if we've reached the threshold
        if (this.events.length >= this.batchSize) {
          const eventsToSend = [...this.events];
          this.sendEvents(eventsToSend);
          this.events = [];
        }
      },
      // Default rrweb options
      inlineStylesheet: true,
      maskAllInputs: true,
      recordCanvas: true,
      collectFonts: true,
    });

    // Set up interval to periodically send events
    this.eventsInterval = setInterval(() => {
      if (this.events.length > 0) {
        const eventsToSend = [...this.events];
        this.sendEvents(eventsToSend);
        this.events = [];
      }
    }, this.batchInterval);

    // Save stop function for later
    this.stopFn = stop || null;
  }

  /**
   * Stop recording the session
   */
  public stop(): void {
    if (!this.metrics.isRecording || !this.stopFn) return;

    // Stop recording
    this.stopFn();
    this.stopFn = null;

    // Clear interval
    if (this.eventsInterval) {
      clearInterval(this.eventsInterval);
      this.eventsInterval = null;
    }

    // Send any remaining events
    if (this.events.length > 0) {
      const finalEvents = [...this.events];
      this.events = [];
      this.sendEvents(finalEvents);
    }

    // Update metrics
    this.updateMetrics({
      isRecording: false
    });
  }

  /**
   * Get current session metrics
   */
  public getMetrics(): SessionMetrics {
    return { ...this.metrics };
  }

  /**
   * Add custom metadata to the session
   */
  public addMetadata(metadata: Record<string, any>): void {
    this.options.metadata = {
      ...this.options.metadata,
      ...metadata
    };
  }

  /**
   * Send events to the Sessionable API
   */
  private async sendEvents(events: rrweb.eventWithTime[]): Promise<void> {
    if (events.length === 0) return;

    this.updateMetrics({
      lastBatchSize: events.length,
      batchesSent: this.metrics.batchesSent + 1
    });

    try {
      // Prepare metadata with basic information
      const metadata = {
        ...this.options.metadata,
        userAgent: navigator.userAgent,
        url: window.location.href,
        timestamp: new Date().toISOString(),
        eventCount: events.length,
      };

      // Prepare payload according to API format
      const payload: SessionPayload = {
        session_id: this.metrics.sessionId,
        events: events,
        metadata: metadata
      };

      // Send to API
      await axios.post(this.apiEndpoint, payload, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.options.apiKey}`,
        }
      });

      // Update metrics on success
      this.updateMetrics({
        connectionStatus: 'connected',
        batchesSuccessful: this.metrics.batchesSuccessful + 1,
        lastSuccessfulUpload: new Date(),
        lastError: null
      });
    } catch (error) {
      // Update metrics on error
      this.updateMetrics({
        connectionStatus: 'error',
        lastError: error instanceof Error ? error.message : 'Unknown error'
      });
      console.error('[Sessionable] Failed to send session data:', error);
    }
  }

  /**
   * Update session metrics
   */
  private updateMetrics(updates: Partial<SessionMetrics>): void {
    this.metrics = {
      ...this.metrics,
      ...updates
    };
  }
}